import React, { useEffect, useState } from 'react';
import { Observer } from 'mobx-react';
import Drawer from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import { useBreakpointDown, useIsCoarseTablet } from '@shared/hooks/useBreakpoint';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getEnv } from 'mobx-state-tree';

import { ThemeProps } from '@styles/theme';
import { IStoresEnv } from '@core/storesEnv';
import { useEntitiesUIStore, useExecutionsGridUIStore, useExecutionsUIStore } from '@core/useStores';
import { ProcedureExecutions } from '../ProcedureExecutions';
import { ExecutionAnalytics } from '../ExecutionAnalytics';
import { NeoAnalyticsSideBar } from '../NeoAnalyticsSideBar/NeoAnalyticsSideBar';
import { Button } from '@shared/components/Button';
import { RoutePaths } from '@core/routes/RoutePaths';
import { performGetProcedure } from 'Procedures/services';
import { useStyles } from '../NeoAnalyticsPage/NeoAnalyticsPage.styles';
import BackIcon from '@assets/left-icon.svg';
import { messages } from 'NeoAnalytics/neoAnalytics.messages';

const DEFAULT_PROCEDURE_WINDOW_DAYS = 30;

export const ProcedureAnalyticsPage: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    // The side panel competes with the grading/analytics content, so touch
    // tablets up to ~11" in landscape also get the drawer; only bigger slates
    // keep the inline sidebar.
    const isBelowTablet = useBreakpointDown('tablet');
    const isCoarseTablet = useIsCoarseTablet();
    const isTabletLayout = isBelowTablet || isCoarseTablet;
    const location = useLocation();
    const navigate = useNavigate();
    const { id, executionId } = useParams();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const entitiesUIStore = useEntitiesUIStore();
    const executionsGridUIStore = useExecutionsGridUIStore();
    const executionsUIStore = useExecutionsUIStore();
    const [procedureName, setProcedureName] = useState<string | null>(null);
    const [procedureImage, setProcedureImage] = useState<string | null>(location.state?.procedureImage ?? null);

    useEffect(() => {
        let cancelled = false;

        if (!id) {
            setProcedureName(null);
            setProcedureImage(null);

            return;
        }

        setProcedureName(null);
        setProcedureImage(null);

        const loadEntity = async () => {
            const [entityResult, procedureResult] = await Promise.all([
                entitiesUIStore.loadEntity({ id }),
                performGetProcedure(
                    getEnv<IStoresEnv>(executionsGridUIStore).api,
                    { payload: { id }, errorHandlers: { DEFAULT: '' } },
                ),
            ]);

            if (cancelled) {return;}

            if (entityResult?.success) {
                setProcedureName(entityResult.data?.name ?? entityResult.data?.entityName ?? null);
            }
            if (procedureResult?.success) {
                setProcedureImage(procedureResult.data?.procedurePreview?.src ?? null);
            }
        };

        void loadEntity();

        return () => {
            cancelled = true;
        };
    }, [entitiesUIStore, executionsGridUIStore, id]);

    useEffect(() => {
        if (!id) {
            return;
        }

        const to = new Date();
        const from = new Date(to);
        from.setDate(from.getDate() - DEFAULT_PROCEDURE_WINDOW_DAYS);

        executionsGridUIStore.clearData();
        executionsUIStore.clearData();

        executionsGridUIStore.clearAnalyticsActorId();
        executionsGridUIStore.setAnalyticsEntityId(id);
        executionsGridUIStore.setParams({
            page: 0,
            limit: 100,
            actorIds: [],
            entityIds: [id],
            fullData: true,
            from,
            to,
        });

        executionsGridUIStore.togglePageActivity(true);

        return () => {
            executionsGridUIStore.togglePageActivity(false);
            executionsGridUIStore.clearData();
            executionsUIStore.clearData();
        };
    }, [id, executionsGridUIStore, executionsUIStore]);

    useEffect(() => {
        if (executionId) {
            void executionsUIStore.loadExecution(executionId);

            return;
        }

        executionsUIStore.clearSelectedExecutionId();
    }, [executionId, executionsUIStore]);

    useEffect(() => {
        if (!isTabletLayout) {
            setSidebarOpen(false);
        }
    }, [isTabletLayout]);

    const goBack = () => {
        if (location.state?.fromProcedureInsights) {
            const insightsId = location.state?.insightsId ?? id;
            navigate(`/${RoutePaths.procedures}/analytics/${insightsId}/insights`);

            return;
        }
        if (location.state?.fromAnalytics) {
            navigate(`/${RoutePaths.analytics}`);

            return;
        }

        if (executionsUIStore.selectedExecutionId && id) {
            navigate(`/${RoutePaths.procedures}/analytics/${id}`);

            return;
        }

        navigate(`/${RoutePaths.procedures}`);
    };

    const goToInsights = () => {
        if (id) {
            navigate(`/${RoutePaths.procedures}/analytics/${id}/insights`);
        }
    };

    return (
        <div className={styles.mainWrapper}>
            {!isTabletLayout && <NeoAnalyticsSideBar mode="procedure" procedureName={procedureName} procedureImage={procedureImage} onBack={goBack} onInsights={goToInsights} />}
            <div className={styles.body}>
                {isTabletLayout && (
                    <div className={styles.tabletSidebarToggleRow}>
                        <Button className={styles.tabletBackButton} type="button" color="secondary" onClick={goBack}>
                            <img src={BackIcon} alt={messages['neoAnalytics.toggle.back']} className={styles.tabletBackButtonIcon} />
                            {messages['neoAnalytics.toggle.back']}
                        </Button>
                        <Button className={styles.tabletSidebarToggle} type="button" onClick={() => setSidebarOpen(true)}>
                            {executionId ? messages['neoAnalytics.toggle.sessionInfo'] : messages['neoAnalytics.toggle.sessions']}
                        </Button>
                        <Button className={styles.tabletSidebarToggle} type="button" onClick={goToInsights}>
                            {messages['neoAnalytics.toggle.insights']}
                        </Button>
                    </div>
                )}
                <Observer>
                    {() => executionsUIStore.selectedExecutionId
                        ? <ExecutionAnalytics />
                        : <ProcedureExecutions standalone hideStandaloneTitle procedureNameOverride={procedureName} />}
                </Observer>
            </div>
            {isTabletLayout && (
                <Drawer
                    anchor="left"
                    open={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                >
                    <NeoAnalyticsSideBar mode="procedure" procedureName={procedureName} procedureImage={procedureImage} onBack={goBack} onInsights={goToInsights} />
                </Drawer>
            )}
        </div>
    );
};